import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Sparkles, Loader2 } from "lucide-react";
import { AdminTabs } from "@/pages/AdminOrdersPage";

const CATEGORIES = {
  "mobile-accessories": ["phone-cases", "chargers", "usb-cables", "power-banks", "earbuds", "screen-protectors", "phone-holders"],
  lifestyle: ["rings", "bracelets", "chains", "wallets"],
};

const EMPTY = {
  name: "", slug: "", description: "", price: 0, compare_price: null,
  category: "mobile-accessories", subcategory: "phone-cases", images: [],
  stock: 100, featured: false, new_arrival: false, best_seller: false,
  rating: 4.7, reviews_count: 0,
};

export default function AdminPage() {
  const { user, loading } = useAuth();
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [genPrompt, setGenPrompt] = useState("");
  const [genLoading, setGenLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (user?.role === "admin") load(); }, [user]);

  async function load() {
    const { data } = await api.get("/products", { params: { limit: 200 } });
    setProducts(data);
  }

  if (loading) return <div className="p-12">Loading…</div>;
  if (!user || user.role !== "admin") return <Navigate to="/" replace />;

  function startNew() {
    setEditing({ ...EMPTY });
    setGenPrompt("");
  }

  async function save() {
    setSaving(true);
    try {
      const body = { ...editing, price: Number(editing.price), compare_price: editing.compare_price ? Number(editing.compare_price) : null, stock: Number(editing.stock) };
      if (editing.id) await api.put(`/products/${editing.id}`, body);
      else await api.post("/products", body);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  }

  async function remove(id) {
    if (!window.confirm("Delete product?")) return;
    await api.delete(`/products/${id}`);
    load();
  }

  async function generateImage() {
    if (!genPrompt) return;
    setGenLoading(true);
    try {
      const { data } = await api.post("/admin/generate-image", { prompt: genPrompt });
      setEditing((e) => ({ ...e, images: [...(e.images || []), data.data_url] }));
      toast.success("Image generated");
    } catch (e) { toast.error(e.response?.data?.detail || "Generation failed"); }
    finally { setGenLoading(false); }
  }

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-12">
      <AdminTabs />
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Admin</div>
          <h1 className="mt-2 font-heading font-medium text-4xl">Products</h1>
        </div>
        <button data-testid="admin-new" onClick={startNew} className="inline-flex items-center gap-1 h-10 px-4 rounded-full bg-electric text-white text-sm font-medium hover:bg-electric-600 transition-colors">
          <Plus className="w-4 h-4" /> New Product
        </button>
      </div>

      <div className="grid gap-2">
        {products.map((p) => (
          <div key={p.id} className="flex items-center gap-3 p-3 rounded-xl border border-border">
            <div className="w-12 h-12 rounded-lg bg-secondary overflow-hidden">
              {p.images?.[0] && <img src={p.images[0]} alt="" className="w-full h-full object-cover" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{p.name}</div>
              <div className="text-xs text-muted-foreground">{p.category} · {p.subcategory} · ₹{p.price}</div>
            </div>
            <button onClick={() => setEditing({ ...p })} data-testid={`admin-edit-${p.slug}`} className="text-xs px-3 py-1.5 rounded-full border border-border hover:border-foreground">Edit</button>
            <button onClick={() => remove(p.id)} className="w-8 h-8 rounded-full text-destructive hover:bg-destructive/10 flex items-center justify-center"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
        {products.length === 0 && <div className="text-sm text-muted-foreground p-6 text-center border border-dashed rounded-xl">No products yet. Click "New Product" to add.</div>}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-background rounded-2xl border border-border w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b sticky top-0 bg-background/95 backdrop-blur-sm">
              <div className="flex items-center justify-between">
                <div className="font-heading font-medium text-xl">{editing.id ? "Edit" : "New"} product</div>
                <button onClick={() => setEditing(null)} className="text-sm text-muted-foreground">Cancel</button>
              </div>
            </div>
            <div className="p-6 space-y-3">
              <input placeholder="Name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value, slug: editing.slug || e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") })} className="w-full h-11 px-4 rounded-lg bg-secondary text-sm outline-none focus:ring-2 focus:ring-electric" data-testid="admin-name" />
              <input placeholder="Slug (url-friendly)" value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} className="w-full h-11 px-4 rounded-lg bg-secondary text-sm outline-none focus:ring-2 focus:ring-electric font-mono" data-testid="admin-slug" />
              <textarea placeholder="Description" rows={3} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="w-full px-4 py-2 rounded-lg bg-secondary text-sm outline-none focus:ring-2 focus:ring-electric" data-testid="admin-desc" />
              <div className="grid grid-cols-2 gap-3">
                <input type="number" placeholder="Price ₹" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} className="h-11 px-4 rounded-lg bg-secondary text-sm outline-none focus:ring-2 focus:ring-electric" data-testid="admin-price" />
                <input type="number" placeholder="Compare price ₹ (optional)" value={editing.compare_price ?? ""} onChange={(e) => setEditing({ ...editing, compare_price: e.target.value ? Number(e.target.value) : null })} className="h-11 px-4 rounded-lg bg-secondary text-sm outline-none focus:ring-2 focus:ring-electric" />
                <select value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value, subcategory: CATEGORIES[e.target.value][0] })} className="h-11 px-4 rounded-lg bg-secondary text-sm outline-none focus:ring-2 focus:ring-electric" data-testid="admin-cat">
                  <option value="mobile-accessories">Mobile Accessories</option>
                  <option value="lifestyle">Lifestyle</option>
                </select>
                <select value={editing.subcategory} onChange={(e) => setEditing({ ...editing, subcategory: e.target.value })} className="h-11 px-4 rounded-lg bg-secondary text-sm outline-none focus:ring-2 focus:ring-electric" data-testid="admin-sub">
                  {CATEGORIES[editing.category].map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <input type="number" placeholder="Stock" value={editing.stock} onChange={(e) => setEditing({ ...editing, stock: e.target.value })} className="h-11 px-4 rounded-lg bg-secondary text-sm outline-none focus:ring-2 focus:ring-electric" />
                <input type="number" step="0.1" placeholder="Rating" value={editing.rating} onChange={(e) => setEditing({ ...editing, rating: Number(e.target.value) })} className="h-11 px-4 rounded-lg bg-secondary text-sm outline-none focus:ring-2 focus:ring-electric" />
              </div>
              <div className="flex flex-wrap gap-4 text-sm">
                {["featured", "new_arrival", "best_seller"].map((k) => (
                  <label key={k} className="inline-flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={editing[k]} onChange={(e) => setEditing({ ...editing, [k]: e.target.checked })} data-testid={`admin-${k}`} />
                    <span>{k.replace(/_/g, " ")}</span>
                  </label>
                ))}
              </div>
              <div className="border-t pt-4">
                <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-2">Images</div>
                <div className="flex flex-wrap gap-2">
                  {editing.images?.map((img, i) => (
                    <div key={i} className="relative w-20 h-20 rounded-lg bg-secondary overflow-hidden">
                      <img src={img} alt="" className="w-full h-full object-cover" />
                      <button onClick={() => setEditing({ ...editing, images: editing.images.filter((_, j) => j !== i) })} className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-black/60 text-white text-xs">×</button>
                    </div>
                  ))}
                </div>
                <input placeholder="Paste image URL and press Enter" onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (e.target.value) { setEditing({ ...editing, images: [...(editing.images || []), e.target.value] }); e.target.value = ""; } } }} className="mt-2 w-full h-10 px-3 rounded-lg bg-secondary text-sm outline-none focus:ring-2 focus:ring-electric" data-testid="admin-img-url" />
                <div className="mt-3 flex gap-2">
                  <input value={genPrompt} onChange={(e) => setGenPrompt(e.target.value)} placeholder="AI prompt: 'minimal black leather wallet on marble background'" className="flex-1 h-10 px-3 rounded-lg bg-secondary text-sm outline-none focus:ring-2 focus:ring-electric" data-testid="admin-gen-prompt" />
                  <button onClick={generateImage} disabled={genLoading} className="h-10 px-4 rounded-lg bg-electric text-white text-sm font-medium hover:bg-electric-600 disabled:opacity-60 flex items-center gap-1" data-testid="admin-gen-btn">
                    {genLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                    Generate
                  </button>
                </div>
              </div>
              <button onClick={save} disabled={saving} className="w-full h-11 rounded-full bg-electric text-white font-medium hover:bg-electric-600 disabled:opacity-60 mt-4" data-testid="admin-save">
                {saving ? "Saving…" : "Save product"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
